import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PermissionsProvider } from "./context/PermissionsContext";
import { ThemeProvider } from "./context/ThemeContext";
import HomePage from "./pages/HomePage";
import K4ResourcePage from "./pages/K4ResourcePage";
import CompliancePage from "./pages/CompliancePage";
import IPCPage from "./pages/IPCPage";
import ModulePage from "./pages/ModulePage";
import SearchPage from "./pages/SearchPage";
import ChatPage from "./pages/ChatPage";
import QuickRefPage from "./pages/QuickRefPage";
import MBSMonitorPage from "./pages/MBSMonitorPage";
import SOPsPage from "./pages/SOPsPage";
import QualityManualPage from "./pages/QualityManualPage";
import UrgentFindingsPage from "./pages/UrgentFindingsPage";
import ClinicalEpisodesPage from "./pages/ClinicalEpisodesPage";
import DifficultPatientsPage from "./pages/DifficultPatientsPage";
import ContrastSOPPage from "./pages/ContrastSOPPage";
import AnaphylaxisSOPPage from "./pages/AnaphylaxisSOPPage";
import RadiationAuditPage from "./pages/RadiationAuditPage";
import LoginActivityPage from "./pages/LoginActivityPage";
import { DisputesListPage, DisputeFormPage, DisputeDetailPage } from "./pages/DisputesPages";
import GovernanceDashboardPage from "./pages/GovernanceDashboardPage";
import WorkCultureSOPPage from "./pages/WorkCultureSOPPage";
import AcceptCultureCharterPage from "./pages/AcceptCultureCharterPage";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/PasswordResetPages";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import ClockPage from "./pages/ClockPage";
import ClaimAdminPage from "./pages/ClaimAdminPage";
import ManagementPage from "./pages/ManagementPage";
import SOPWalkthroughPage from "./pages/SOPWalkthroughPage";
import PreparationPage from "./pages/PreparationPage";
import PreparationPrintAllPage from "./pages/PreparationPrintAllPage";
import ReferralBasePage from "./pages/ReferralBasePage";
// Equipment page is now embedded as a tab in CompliancePage (iter 77).
// /equipment redirects to /compliance?tab=equipment.
import RadiationGovernancePage from "./pages/RadiationGovernancePage";
import PracticeSettingsPage from "./pages/PracticeSettingsPage";
import DIASChecklistPage from "./pages/DIASChecklistPage";
import PeoplePage from "./pages/PeoplePage";
import OnboardingWizardPage from "./pages/OnboardingWizardPage";
import QIPSharePage from "./pages/QIPSharePage";
import { ScreeningIndexPage, ScreeningFormPage } from "./pages/ScreeningPages";
import { IncidentsIndexPage, IncidentReportPage, IncidentDetailPage, NearMissPosterPage } from "./pages/IncidentPages";
import { ConsentIndexPage, ConsentFormPage, ConsentSubmissionPage, PostProcedureCareLeafletPage } from "./pages/ConsentPages";
import AnticoagPage from "./pages/AnticoagPage";
import AnticoagScreeningPage from "./pages/AnticoagScreeningPage";
import LetterheadPage from "./pages/LetterheadPage";
import GoLivePage from "./pages/GoLivePage";
import RosterPage, { RosterRespondPage, RosterPublicSchedulePage, SwapRespondPage } from "./pages/RosterPages";
import InvoicesPage, { InvoiceEditorPage } from "./pages/InvoicesPage";
import { ClinicalHub, KnowledgeHub, OperationsHub } from "./pages/HubPages";
import PermissionsPage from "./pages/PermissionsPage";
import HelpPage from "./pages/HelpPage";
import BrandFooter from "./components/BrandFooter";
import LoginPage from "./pages/LoginPage";
import ProfilePage from "./pages/ProfilePage";
import SignContractPage from "./pages/SignContractPage";
import ChatBubble from "./components/ChatBubble";
import OnboardingBanner from "./components/OnboardingBanner";
import QuickSearchPalette from "./components/QuickSearchPalette";
import PracticeCheckReminder from "./components/PracticeCheckReminder";
import { Toaster as SonnerToaster } from "sonner";

const CHARTER_VERSION = "1.0";

function hasAcceptedCharter(user) {
  return !!user?.culture_charter_accepted_at && user?.culture_charter_version === CHARTER_VERSION;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-[#52c3c5] border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAcceptedCharter(user)) return <Navigate to="/accept-charter" replace />;
  // Iter 130 — Force change of password after admin-initiated reset.
  // The user can only access /change-password until they pick a new one.
  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return (
    <>
      {children}
      <BrandFooter />
    </>
  );
}

function PMRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAcceptedCharter(user)) return <Navigate to="/accept-charter" replace />;
  if (user.role !== 'admin' && user.role !== 'superuser') return <Navigate to="/" replace />;
  return (
    <>
      {children}
      <BrandFooter />
    </>
  );
}

function CharterAcceptRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (hasAcceptedCharter(user)) return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAcceptedCharter(user)) return <Navigate to="/accept-charter" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<LoginPage />} />
        <Route path="/qip-share/:token" element={<QIPSharePage />} />
        <Route path="/accept-charter" element={<CharterAcceptRoute><AcceptCultureCharterPage /></CharterAcceptRoute>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/claim-admin" element={<ProtectedRoute><ClaimAdminPage /></ProtectedRoute>} />
        <Route path="/management" element={<AdminRoute><ManagementPage /></AdminRoute>} />
        <Route path="/sops/walkthrough/:assignmentId" element={<ProtectedRoute><SOPWalkthroughPage /></ProtectedRoute>} />
        <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        <Route path="/clinical" element={<ProtectedRoute><ClinicalHub /></ProtectedRoute>} />
        <Route path="/knowledge" element={<ProtectedRoute><KnowledgeHub /></ProtectedRoute>} />
        <Route path="/operations" element={<ProtectedRoute><OperationsHub /></ProtectedRoute>} />
        <Route path="/permissions" element={<ProtectedRoute><PermissionsPage /></ProtectedRoute>} />
        <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
        <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
        <Route path="/clock" element={<ProtectedRoute><ClockPage /></ProtectedRoute>} />
        <Route path="/k4" element={<ProtectedRoute><K4ResourcePage /></ProtectedRoute>} />
        <Route path="/compliance" element={<ProtectedRoute><CompliancePage /></ProtectedRoute>} />
        <Route path="/ipc" element={<ProtectedRoute><IPCPage /></ProtectedRoute>} />
        <Route path="/preparation" element={<ProtectedRoute><PreparationPage /></ProtectedRoute>} />
        <Route path="/preparation/print-all" element={<ProtectedRoute><PreparationPrintAllPage /></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute><ReferralBasePage /></ProtectedRoute>} />
        <Route path="/anticoag" element={<ProtectedRoute><AnticoagPage /></ProtectedRoute>} />
        <Route path="/anticoag-screening" element={<ProtectedRoute><AnticoagScreeningPage /></ProtectedRoute>} />
        <Route path="/letterhead" element={<ProtectedRoute><LetterheadPage /></ProtectedRoute>} />
        <Route path="/golive" element={<ProtectedRoute><GoLivePage /></ProtectedRoute>} />
        <Route path="/roster" element={<ProtectedRoute><RosterPage /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
        <Route path="/invoices/:invnum" element={<ProtectedRoute><InvoiceEditorPage /></ProtectedRoute>} />
        <Route path="/roster/respond/:token" element={<RosterRespondPage />} />
        <Route path="/roster/swap-respond/:token" element={<SwapRespondPage />} />
        <Route path="/roster/public/:token" element={<RosterPublicSchedulePage />} />
        <Route path="/equipment" element={<Navigate to="/compliance?tab=equipment" replace />} />
        <Route path="/radiation-governance" element={<ProtectedRoute><RadiationGovernancePage /></ProtectedRoute>} />
        <Route path="/practice-settings" element={<ProtectedRoute><PracticeSettingsPage /></ProtectedRoute>} />
        <Route path="/dias-checklist" element={<PMRoute><DIASChecklistPage /></PMRoute>} />
        <Route path="/people" element={<PMRoute><PeoplePage /></PMRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizardPage /></ProtectedRoute>} />
        <Route path="/screening" element={<ProtectedRoute><ScreeningIndexPage /></ProtectedRoute>} />
        <Route path="/screening/new/:formId" element={<ProtectedRoute><ScreeningFormPage /></ProtectedRoute>} />
        <Route path="/incidents" element={<ProtectedRoute><IncidentsIndexPage /></ProtectedRoute>} />
        <Route path="/incidents/new" element={<ProtectedRoute><IncidentReportPage /></ProtectedRoute>} />
        <Route path="/incidents/culture-poster" element={<ProtectedRoute><NearMissPosterPage /></ProtectedRoute>} />
        <Route path="/incidents/:id" element={<ProtectedRoute><IncidentDetailPage /></ProtectedRoute>} />
        <Route path="/consent" element={<ProtectedRoute><ConsentIndexPage /></ProtectedRoute>} />
        <Route path="/consent/submission/:id" element={<ProtectedRoute><ConsentSubmissionPage /></ProtectedRoute>} />
        <Route path="/consent/:templateId/post-procedure-care" element={<ProtectedRoute><PostProcedureCareLeafletPage /></ProtectedRoute>} />
        <Route path="/consent/:templateId" element={<ProtectedRoute><ConsentFormPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<Navigate to="/management" replace />} />
        <Route path="/dashboard/logins" element={<PMRoute><LoginActivityPage /></PMRoute>} />
        <Route path="/users" element={<Navigate to="/management" replace />} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/module/:slug" element={<ProtectedRoute><ModulePage /></ProtectedRoute>} />
        <Route path="/quick-ref/:slug" element={<ProtectedRoute><QuickRefPage /></ProtectedRoute>} />
        <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
        <Route path="/mbs" element={<ProtectedRoute><MBSMonitorPage /></ProtectedRoute>} />
        <Route path="/sops" element={<ProtectedRoute><SOPsPage /></ProtectedRoute>} />
        <Route path="/sops/quality-manual" element={<ProtectedRoute><QualityManualPage /></ProtectedRoute>} />
        <Route path="/sops/urgent-findings" element={<ProtectedRoute><UrgentFindingsPage /></ProtectedRoute>} />
        <Route path="/sops/clinical-episodes" element={<ProtectedRoute><ClinicalEpisodesPage /></ProtectedRoute>} />
        <Route path="/sops/difficult-patients" element={<ProtectedRoute><DifficultPatientsPage /></ProtectedRoute>} />
        <Route path="/sops/contrast" element={<ProtectedRoute><ContrastSOPPage /></ProtectedRoute>} />
        <Route path="/sops/anaphylaxis" element={<ProtectedRoute><AnaphylaxisSOPPage /></ProtectedRoute>} />
        <Route path="/sops/governance" element={<ProtectedRoute><GovernanceDashboardPage /></ProtectedRoute>} />
        <Route path="/sops/work-culture" element={<ProtectedRoute><WorkCultureSOPPage /></ProtectedRoute>} />
        <Route path="/sops/radiation-audit" element={<ProtectedRoute><RadiationAuditPage /></ProtectedRoute>} />
        <Route path="/contracts/sign/:token" element={<SignContractPage />} />
        <Route path="/disputes" element={<ProtectedRoute><DisputesListPage /></ProtectedRoute>} />
        <Route path="/disputes/new" element={<ProtectedRoute><DisputeFormPage /></ProtectedRoute>} />
        <Route path="/disputes/:id" element={<ProtectedRoute><DisputeDetailPage /></ProtectedRoute>} />
      </Routes>
      {user && !window.location.pathname.startsWith('/contracts/sign/') && <ChatBubble />}
      {user && !window.location.pathname.startsWith('/contracts/sign/') && <OnboardingBanner />}
      {user && !window.location.pathname.startsWith('/contracts/sign/') && <QuickSearchPalette />}
      {user && !window.location.pathname.startsWith('/contracts/sign/') && <PracticeCheckReminder />}
      <SonnerToaster richColors closeButton position="top-right" />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PermissionsProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </PermissionsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
